import type { FilterRelation } from "@eu.mdotm.api/sphere";
import { type Option, Controller, Icon, Transition } from "@mdotm/shadcn/legacy/components";
import { DatePickerInput, NumberInput, Select, Button, IconButton } from "@mdotm/shadcn/mdotm/components";
import { type NodeOrFn, overrideClassName, renderNodeOrFn } from "@mdotm/shadcn/legacy/react-extensions";
import { ForEach } from "@mdotm/utils/react-extra";
import { themeCSSVars } from "@mdotm/shadcn/legacy/themes";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

export type CustomFilterValues<TFieldKeys extends string, TFieldType extends string> = {
	key?: TFieldKeys;
	type?: TFieldType;
	relation?: FilterRelation;
	value?: Array<string>;
};

export type CustomFilterOption<TFieldKeys extends string, TFieldType extends string> = {
	key: TFieldKeys;
	type: TFieldType;
	needApi?: boolean;
	availableRelations: FilterRelation[];
};

export function MultiFilter<TFieldKeys extends string, TFieldType extends string>({
	value,
	onChange,
	onDelete,
	options,
	fieldSetBlock,
}: {
	value: CustomFilterValues<TFieldKeys, TFieldType>[];
	onDelete(index: number): void;
	onChange(filter: CustomFilterValues<TFieldKeys, TFieldType>, index: number): void;
	options: CustomFilterOption<TFieldKeys, TFieldType>[];
	fieldSetBlock: NodeOrFn<{
		filterType: TFieldType;
		onChange(filter: CustomFilterValues<TFieldKeys, TFieldType>, index: number): void;
		value: CustomFilterValues<TFieldKeys, TFieldType>;
		index: number;
	}>;
}): React.JSX.Element {
	const { t } = useTranslation();
	const filterKeyMap = t("METRIC_COMPARISON_CHART", { returnObjects: true });
	const availableFilters = useMemo<Partial<{ [K in TFieldKeys]: CustomFilterOption<TFieldKeys, TFieldType> }>>(() => {
		if (!options) {
			return {} as Partial<{ [K in TFieldKeys]: CustomFilterOption<TFieldKeys, TFieldType> }>;
		}

		return options
			.filter((x) => x.key)
			.reduce<Partial<{ [K in TFieldKeys]: CustomFilterOption<TFieldKeys, TFieldType> }>>(
				(map, filter) => ({ ...map, [filter.key]: filter }),
				{},
			);
	}, [options]);

	const availableFilterKeys = useMemo<Array<Option<TFieldKeys>>>(() => {
		if (!options) {
			return [];
		}
		return options
			.filter((x) => x.key)
			.map((x) => {
				const label = filterKeyMap[x.key as keyof typeof filterKeyMap] ?? x.key;
				return {
					label,
					value: x.key,
				};
			});
	}, [options, filterKeyMap]);

	return (
		<ForEach collection={value}>
			{(opt, index) => (
				<Transition
					in
					initialIn={false}
					className="transition-[opacity,transform,scale] origin-top"
					enterFromClassName="opacity-0 scale-95"
					enterToClassName="opacity-100 scale-100"
				>
					{({ className }) => (
						<div className={overrideClassName(className, "flex items-center")}>
							<div className="flex gap-2 flex-1 max-w-[calc(100%-38px)]">
								<Select
									options={availableFilterKeys}
									value={opt.key}
									enableSearch
									onChange={(newKey) => {
										if (newKey) {
											onChange(
												{
													key: newKey,
													type: availableFilters[newKey]!.type,
													relation: undefined,
													value: undefined,
												},
												index,
											);
										}
									}}
									className="w-[139px]"
								/>

								{(() => {
									const canRenderSection = opt.key && opt.type;
									if (!canRenderSection) {
										return <></>;
									}

									const filterOption = availableFilters[opt.key!];
									if (filterOption?.needApi) {
										return renderNodeOrFn(fieldSetBlock, { filterType: opt.type!, onChange, value: opt, index });
									}

									if (opt.type === "DATE" || opt.type === "NUMERIC" || opt.type === "PERCENTAGE") {
										const options =
											availableFilters[opt.key!]!.availableRelations.flatMap((relation) =>
												relation
													? [
															{
																label:
																	opt.type === "DATE" ? t(`RELATION.CALENDAR.${relation}`) : t(`RELATION.${relation}`),
																value: relation,
															},
														]
													: [],
											) ?? [];
										return (
											<Select
												options={options}
												value={opt.relation}
												onChange={(relation) => {
													if (relation) {
														onChange({ ...opt, relation }, index);
													}
												}}
												className="w-[139px]"
											/>
										);
									}

									if (opt.type === "BOOLEAN") {
										return (
											<Select
												options={[
													{ label: "Yes", value: true },
													{ label: "No", value: false },
												]}
												value={opt.value ? opt.value[0] === "true" : undefined}
												onChange={(flag) => {
													onChange(
														{
															...opt,
															value: [String(flag)],
															relation: "EQUAL",
														},
														index,
													);
												}}
												className="w-[139px]"
											/>
										);
									}
									return <></>;
								})()}

								{(() => {
									const canRenderSection = opt.key && opt.relation && opt.type;
									if (!canRenderSection) {
										return <></>;
									}

									if (opt.type === "PERCENTAGE") {
										return (
											<Controller
												value={opt.value?.[0] ? Number(opt.value?.[0]) : null}
												onChange={(value) =>
													onChange({ ...opt, value: value != null ? [String(value)] : undefined }, index)
												}
											>
												{({ value: percentage, onChange: onChangePercentage, onCommit }) => (
													<NumberInput
														value={percentage}
														rightContent={<Icon icon="Percentile" />}
														placeholder="0"
														onChange={onChangePercentage}
														className="w-24"
														onBlur={() => onCommit()}
													/>
												)}
											</Controller>
										);
									}
									if (opt.type === "NUMERIC") {
										return (
											<Controller
												value={opt.value?.[0] ? Number(opt.value?.[0]) : null}
												onChange={(value) =>
													onChange({ ...opt, value: value != null ? [String(value)] : undefined }, index)
												}
											>
												{({ value: number, onChange: onChangeNumber, onCommit }) => (
													<NumberInput
														value={number}
														placeholder="0"
														onChange={onChangeNumber}
														className="w-24"
														onBlur={() => onCommit()}
													/>
												)}
											</Controller>
										);
									}
									if (opt.type === "DATE") {
										return (
											<DatePickerInput
												value={opt.value?.[0] ?? ""}
												className="w-24"
												onChange={(newDate) => {
													onChange(
														{
															...opt,
															value: newDate ? [new Date(newDate).toISOString()] : undefined,
														},
														index,
													);
												}}
											/>
										);
									}
									return <></>;
								})()}
							</div>
							<IconButton
								icon="Delete"
								size={18}
								color={themeCSSVars.palette_P400}
								className="ml-auto"
								onClick={() => onDelete(index)}
							/>
						</div>
					)}
				</Transition>
			)}
		</ForEach>
	);
}

export function ButtonMultiFilter(props: { onClick(): void }): React.JSX.Element {
	return (
		<Button palette="secondary" onClick={props.onClick} size="small">
			<Icon icon="Outline1" />
		</Button>
	);
}
